
import React from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import './calendar.css';

const CustomCalendar = ({ 
  events = [], 
  date, 
  onAppointmentClick, 
  startTime = '08:00',
  endTime = '20:00'
}) => {
  const getStatusColors = (status) => {
    const statusColors = {
      10: { 
        bgColor: "#ffffff",
        borderColor: "#FF0000",
        textColor: "#FF0000"
      },
      11: { 
        bgColor: "#ffffff",
        borderColor: "#92007E",
        textColor: "#92007E"
      },
      14: { 
        bgColor: "#ffffff",
        borderColor: "#CC0000",
        textColor: "#990000"
      },
      12: { 
        bgColor: "#ffffff",
        borderColor: "#037FE8",
        textColor: "#037FE8"
      },
      15: { 
        bgColor: "#ffffff",
        borderColor: "#CCCC00",
        textColor: "#666600"
      },
      default: { 
        bgColor: "#ffffff",
        borderColor: "#999999",
        textColor: "#666666"
      }
    };
    return statusColors[status] || statusColors.default;
  };
  
  function formatTimeForCalendar(timeString, addHour = false) {
    if (!timeString) return addHour ? '21:00:00' : '08:00:00';
    
    timeString = timeString.toString().replace(/[AP]M/i, '').trim();
    let [hours, minutes] = timeString.split(':');
    hours = parseInt(hours, 10);
    minutes = minutes ? parseInt(minutes, 10) : 0;
    
    if (addHour) {
      hours += 1;
      if (hours >= 24) {
        hours = 23;
        minutes = 59;
      }
    }
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  }

  const formattedStart = formatTimeForCalendar(startTime);
  const formattedEnd = formatTimeForCalendar(endTime, true);

  const filteredEvents = events.filter(event => {
    if (!event.start || !event.end) return false;
    
    const eventStart = new Date(event.start).getHours() + 
                      new Date(event.start).getMinutes()/60;
    const eventEnd = new Date(event.end).getHours() + 
                    new Date(event.end).getMinutes()/60;
    const calendarStart = parseInt(formattedStart.split(':')[0]) + 
                         parseInt(formattedStart.split(':')[1])/60;
    const calendarEnd = parseInt(formattedEnd.split(':')[0]) + 
                       parseInt(formattedEnd.split(':')[1])/60;
    
    const duration = (eventEnd - eventStart) * 60;
    return duration >= 15 && eventStart >= calendarStart && eventEnd <= calendarEnd;
  });

  function renderEventContent(eventInfo) {
    const { timeText, event } = eventInfo;
    const status = event.extendedProps?.status;
    const statusColors = getStatusColors(status);
    const statusName = event.extendedProps?.status_name || '';

    return (
      <div className="custom-event">
        <div className="event-time">
          {timeText}
          {statusName && (
            <span 
              className="event-status"
              style={{
                backgroundColor: statusColors.bgColor,
                color: statusColors.textColor,
                border: `1px solid ${statusColors.borderColor}`,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '500',
                marginLeft: '6px'
              }}
            >
              {statusName}
            </span>
          )}
        </div>
        <div className="event-title">{event.title}</div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <FullCalendar
        key={date}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView="timeGridDay"
        initialDate={date}
        eventOverlap={false}
        eventOrder="title"
        slotDuration="00:15:00"
        slotLabelFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short',
        }}
        headerToolbar={false}
        allDaySlot={false}
        height="auto"
        events={filteredEvents}
        slotMinTime={formattedStart}
        slotMaxTime={formattedEnd}
        editable={false}
        selectable={false}
        nowIndicator={false}
        dayHeaderFormat={{
          weekday: 'long',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }}
        contentHeight="auto"
        expandRows={true}
        eventContent={renderEventContent}
        eventClick={(info) => {
          const details = info.event.extendedProps;
          const appointment = { details };
          if (onAppointmentClick) {
            onAppointmentClick(appointment, date);
          }
        }}
      />
    </div>
  );
};

export default CustomCalendar;